import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CornerDownRight, Plus, Star } from 'lucide-react';
import { z } from 'zod';
import { PROJECT_HEALTHS, PROJECT_STATUSES, setProjectFavourite } from '@mkcore/projects/schema';
import { Badge, Button, Checkbox, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton, cn } from '@mkcore/ui';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { projectsQuery } from '@/features/projects/queries';
import { ColourDot, HealthBadge, ProjectStatusBadge } from '@/features/projects/labels';
import { NewProjectDialog } from '@/features/projects/new-project';
import { DataTable, type ColumnDef } from '@/components/data-table';
import { formatDate } from '@/lib/format';
import { ProjectsOverview } from '@/features/overviews';

const search = z.object({
  q: z.string().optional(),
  status: z.enum(PROJECT_STATUSES).optional(),
  health: z.enum(PROJECT_HEALTHS).optional(),
  organisationId: z.string().optional(),
  engagementId: z.string().optional(),
  favourite: z.boolean().optional(),
  template: z.boolean().optional(),
  archived: z.boolean().optional(),
});

export const Route = createFileRoute('/_app/projects/')({ validateSearch: search, component: Projects });

/**
 * Projects and subprojects (CO-0189, CO-1268), with colour, status and favourites (CO-0191, CO-0195). The server puts
 * each subproject right under its main project (#293); sorted, filtered or grouped away from it, it shows the name of
 * its main project.
 */
function Projects() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const canCreate = useCan('projects:create');
  const [open, setOpen] = useState(false);
  const q = useInfiniteQuery(
    projectsQuery({
      q: s.q || undefined,
      status: s.status ? [s.status] : undefined,
      health: s.health ? [s.health] : undefined,
      organisationId: s.organisationId || undefined,
      engagementId: s.engagementId || undefined,
      favourite: s.favourite || undefined,
      template: s.template || undefined,
      includeArchived: s.archived || undefined,
    }),
  );
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const set = (patch: Partial<z.infer<typeof search>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const favourite = useActionMutation(setProjectFavourite, { invalidate: [['project']] });
  const columns = useMemo<ColumnDef<(typeof rows)[number]>[]>(
    () => [
      {
        id: 'favourite',
        label: t('projects.favouriteColumn'),
        type: 'enum',
        options: [
          { value: 'yes', label: t('common.yes') },
          { value: 'no', label: t('common.no') },
        ],
        value: (p) => (p.favourite ? 'yes' : 'no'),
        className: 'w-8',
        cell: (p) => (
          <button
            type="button"
            aria-label={p.favourite ? t('projects.unfavourite') : t('projects.favourite')}
            aria-pressed={p.favourite}
            className={p.favourite ? 'text-gold' : 'text-fg-3 hover:text-fg-1'}
            onClick={() => favourite.mutate({ input: { id: p.id, favourite: !p.favourite } })}
          >
            <Star className="size-4" fill={p.favourite ? 'currentColor' : 'none'} />
          </button>
        ),
      },
      {
        id: 'name',
        label: t('common.name'),
        value: (p) => p.name,
        cell: (p, { nested }) => (
          <span className={cn('flex items-center gap-2', nested && 'pl-4')}>
            {p.parentId && <CornerDownRight className="size-3.5 text-fg-3" aria-label={t('projects.subprojectOf')} />}
            <ColourDot colour={p.colour} />
            {p.parentName && !nested && <span className="text-fg-3">{p.parentName} ›</span>}
            <Link to="/projects/$id" params={{ id: p.id }} className="font-bold hover:underline">
              {p.name}
            </Link>
            {p.code && <span className="text-xs text-fg-3">{p.code}</span>}
            {p.isTemplate && <Badge tone="info">{t('projects.template')}</Badge>}
            {p.archivedAt && <Badge tone="neutral">{t('org.archived')}</Badge>}
          </span>
        ),
      },
      { id: 'code', label: t('projects.fields.code'), hidden: true, value: (p) => p.code, cell: (p) => p.code ?? '–' },
      {
        id: 'organisation',
        label: t('org.eyebrow'),
        groupable: true,
        value: (p) => p.organisationName ?? t('projects.internal'),
        cell: (p) => <span className="text-fg-2">{p.organisationName ?? t('projects.internal')}</span>,
        className: 'hidden md:table-cell',
      },
      { id: 'engagement', label: t('nav.engagements'), groupable: true, value: (p) => p.engagementName, cell: (p) => <span className="text-fg-2">{p.engagementName ?? '–'}</span>, className: 'hidden lg:table-cell' },
      {
        id: 'status',
        label: t('common.status'),
        type: 'enum',
        groupable: true,
        options: PROJECT_STATUSES.map((x) => ({ value: x, label: t(`projects.status.${x}`) })),
        value: (p) => p.status,
        cell: (p) => <ProjectStatusBadge status={p.status} />,
      },
      {
        id: 'health',
        label: t('projects.fields.health'),
        type: 'enum',
        groupable: true,
        options: PROJECT_HEALTHS.map((x) => ({ value: x, label: t(`projects.health.${x}`) })),
        value: (p) => p.health,
        cell: (p) => (p.health ? <HealthBadge health={p.health} note={p.healthNote} /> : <span className="text-fg-3">–</span>),
        className: 'hidden sm:table-cell',
      },
      {
        id: 'billable',
        label: t('projects.billable'),
        type: 'enum',
        groupable: true,
        options: [
          { value: 'yes', label: t('common.yes') },
          { value: 'no', label: t('common.no') },
        ],
        value: (p) => (p.effectiveBillableDefault ? 'yes' : 'no'),
        cell: (p) => <span className="text-fg-2">{p.effectiveBillableDefault ? t('common.yes') : t('common.no')}</span>,
        className: 'hidden sm:table-cell',
      },
      { id: 'startsOn', label: t('engagements.fields.startsOn'), type: 'date', hidden: true, value: (p) => p.startsOn, cell: (p) => (p.startsOn ? formatDate(p.startsOn) : '–') },
      { id: 'endsOn', label: t('engagements.fields.endsOn'), type: 'date', hidden: true, value: (p) => p.endsOn, cell: (p) => (p.endsOn ? formatDate(p.endsOn) : '–') },
      { id: 'budgetHours', label: t('engagements.fields.budgetHours'), type: 'number', hidden: true, value: (p) => (p.budgetMinutes === null ? null : p.budgetMinutes / 60), cell: (p) => (p.budgetMinutes === null ? '–' : p.budgetMinutes / 60), className: 'text-right tabular' },
    ],
    [t, favourite],
  );

  return (
    <>
      <PageHeader
        title={t('projects.title')}
        description={t('projects.description')}
        actions={
          canCreate && (
            <Button onClick={() => setOpen(true)}>
              <Plus /> {t('projects.new')}
            </Button>
          )
        }
      />
      <ProjectsOverview />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('common.search')} className="w-64">
          {(p) => <Input {...p} value={s.q ?? ''} placeholder={t('projects.searchHint')} onChange={(e) => set({ q: e.target.value || undefined })} />}
        </Field>
        <Field label={t('common.status')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.status ?? ''} onChange={(e) => set({ status: (e.target.value || undefined) as never })}>
              <option value="">{t('common.all')}</option>
              {PROJECT_STATUSES.map((v) => (
                <option key={v} value={v}>
                  {t(`projects.status.${v}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('projects.fields.health')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.health ?? ''} onChange={(e) => set({ health: (e.target.value || undefined) as never })}>
              <option value="">{t('common.all')}</option>
              {PROJECT_HEALTHS.map((v) => (
                <option key={v} value={v}>
                  {t(`projects.health.${v}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <Checkbox checked={!!s.favourite} onCheckedChange={(v) => set({ favourite: v === true || undefined })} /> {t('projects.onlyFavourites')}
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <Checkbox checked={!!s.template} onCheckedChange={(v) => set({ template: v === true || undefined })} /> {t('projects.onlyTemplates')}
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <Checkbox checked={!!s.archived} onCheckedChange={(v) => set({ archived: v === true || undefined })} /> {t('org.includeArchived')}
        </label>
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={t('projects.empty')} description={canCreate ? t('projects.emptyHint') : undefined} />
      ) : (
        <DataTable listKey="project.project.list" columns={columns} rows={rows} rowKey={(p) => p.id} parentKey={(p) => p.parentId} onOpen={(p) => void navigate({ to: '/projects/$id', params: { id: p.id } })} partial={q.hasNextPage} onNeedAll={() => q.hasNextPage && !q.isFetchingNextPage && void q.fetchNextPage()} />
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
      <NewProjectDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
